import React, { useState, useEffect } from 'react';
import './TreatmentPlanEditor.css';
import { apiPost, apiPut, getProcedureCatalog } from '../../../services/api';
import Button from '../../common/Button';

interface Procedure {
    id: number;
    code: string;
    name: string;
    category: string;
    default_fee: number;
    requires_tooth: boolean;
}

interface PlanItem {
    key: number;
    procedure_id: number | null;
    procedure_name: string;
    tooth_no: string;
    cost: number;
}

interface TreatmentPlan {
    id: number;
    total_cost: number;
    discount: number;
    final_cost: number;
    status: string;
    notes: string;
    items: any[];
}

interface Props {
    patientId: string;
    visitId?: number;
    existingPlan: TreatmentPlan | null;
    onSave: () => void;
    onCancel: () => void;
}

const TreatmentPlanEditor: React.FC<Props> = ({ patientId, visitId, existingPlan, onSave, onCancel }) => {
    const [procedures, setProcedures] = useState<Procedure[]>([]);
    const [items, setItems] = useState<PlanItem[]>([
        { key: Date.now(), procedure_id: null, procedure_name: '', tooth_no: '', cost: 0 }
    ]);
    const [notes, setNotes] = useState('');
    const [discount, setDiscount] = useState(0);

    useEffect(() => {
        getProcedureCatalog().then((data: any) => {
            const list = Object.values(data) as Procedure[];
            setProcedures(list.sort((a, b) => a.name.localeCompare(b.name)));
        }).catch(err => console.error("Failed to load procedures", err));

        if (existingPlan) {
            setNotes(existingPlan.notes || '');
            setDiscount(existingPlan.discount || 0);
            setItems(existingPlan.items.map((i, idx) => ({
                key: idx,
                procedure_id: i.procedure_id,
                procedure_name: i.procedure_name,
                tooth_no: i.tooth_no,
                cost: i.cost
            })));
        }
    }, [existingPlan]);

    const addItem = () => {
        setItems([...items, { key: Date.now(), procedure_id: null, procedure_name: '', tooth_no: '', cost: 0 }]);
    };

    const removeItem = (index: number) => {
        const newItems = [...items];
        newItems.splice(index, 1);
        setItems(newItems);
    };

    const updateItem = (index: number, field: keyof PlanItem, value: any) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: value };

        // Auto-fill cost/tooth requirement
        if (field === 'procedure_id') {
            const proc = procedures.find(p => p.id === Number(value));
            if (proc) {
                newItems[index].procedure_name = proc.name;
                newItems[index].cost = proc.default_fee;
                // create boolean flag for tooth req? For now just rely on user.
            }
        }

        setItems(newItems);
    };

    const totalCost = items.reduce((sum, item) => sum + (Number(item.cost) || 0), 0);
    const finalCost = Math.max(0, totalCost - (Number(discount) || 0));

    const handleSave = async () => {
        const payload = {
            patient_id: patientId,
            visit_id: visitId,
            items: items.filter(i => i.procedure_name), // Skip empty
            notes,
            discount
        };

        const endpoint = existingPlan
            ? `/api/treatment-plans/${existingPlan.id}`
            : `/api/treatment-plans`;

        try {
            if (existingPlan) {
                await apiPut(endpoint, payload);
            } else {
                await apiPost(endpoint, payload);
            }
            onSave();
        } catch (e) {
            console.error(e);
            alert("Error saving plan");
        }
    };

    return (
        <div className="tpe-container">
            <div className="tpe-header">
                <h3>{existingPlan ? 'Edit Plan' : 'New Treatment Plan'}</h3>
                <div className="tpe-header-actions">
                    <Button variant="ghost" onClick={onCancel}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>Save Plan</Button>
                </div>
            </div>

            <div className="tpe-form tpe-form-card">
                <div className="tpe-grid tpe-grid-head">
                    <div className="tpe-col-title">Procedure</div>
                    <div className="tpe-col-title">Tooth</div>
                    <div className="tpe-col-title">Cost (₹)</div>
                    <div></div>

                    {items.map((item, idx) => (
                        <React.Fragment key={item.key}>
                            <div>
                                <select
                                    value={item.procedure_id || ''}
                                    onChange={e => updateItem(idx, 'procedure_id', e.target.value)}
                                    className="tpe-select"
                                >
                                    <option value="">Select Procedure...</option>
                                    {Object.entries(
                                        procedures.reduce((acc, p) => {
                                            const cat = p.category || 'Other';
                                            if (!acc[cat]) acc[cat] = [];
                                            acc[cat].push(p);
                                            return acc;
                                        }, {} as Record<string, Procedure[]>)
                                    ).map(([category, procs]) => (
                                        <optgroup key={category} label={category}>
                                            {procs.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <input
                                    type="text"
                                    value={item.tooth_no}
                                    onChange={e => updateItem(idx, 'tooth_no', e.target.value)}
                                    placeholder="ex: 11,21"
                                    className="tpe-input"
                                />
                            </div>
                            <div>
                                <input
                                    type="number"
                                    value={item.cost}
                                    onChange={e => updateItem(idx, 'cost', e.target.value)}
                                    className="tpe-input"
                                />
                            </div>
                            <div className="tpe-remove-cell">
                                <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="tpe-remove-btn">×</Button>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
                <Button variant="outline" size="sm" onClick={addItem} className="tpe-add-btn">+ Add Item</Button>

                <div className="tpe-summary">
                    <div className="tpe-row">
                        <label>Notes:</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} />
                    </div>
                    <div className="tpe-totals">
                        <div className="tpe-calc-row">
                            <span>Subtotal:</span>
                            <span>₹{totalCost}</span>
                        </div>
                        <div className="tpe-calc-row">
                            <span>Discount:</span>
                            <input
                                type="number"
                                value={discount}
                                onChange={e => setDiscount(Number(e.target.value))}
                            />
                        </div>
                        <div className="tpe-calc-row total">
                            <span>Final Total:</span>
                            <span>₹{finalCost}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TreatmentPlanEditor;
